import {
  AspBill,
  AspDocumentStatus,
  AspInvoice,
  AspPaymentDetail,
  BillOrInvoiceRailz,
  PaymentMethod,
  TemporaryBillsOrInvoicesRails,
} from 'vopay';

// Extra asp identifiers carried alongside the legacy row shape so the pay,
// cancel and credit-note flows can address the asp endpoints.
export type AspRowExtras = {
  AspDocumentID: number;
  AspContactID: number | null;
  AspConnectionID: number;
  AspClientAccountID: string | null;
  AspPaymentMethods: PaymentMethod[];
  // Set when the document's newest payment failed. Drives the row's retry
  // state; the failure reason itself isn't in the asp list response — it lives
  // on the VoPay transaction and is fetched lazily for visible rows.
  AspFailedPayment: Pick<AspPaymentDetail, 'TransactionID' | 'TransactionDate'> | null;
};

export type AspAccountingRow = BillOrInvoiceRailz & AspRowExtras;

// Rows flow through the legacy Temporary* mapping via object spread, which
// preserves these extras at runtime but not in the static type — recover them
// with a runtime check instead of a cast.
export function getAspRowExtras(
  row: Partial<AspRowExtras> | TemporaryBillsOrInvoicesRails | BillOrInvoiceRailz,
): AspRowExtras | null {
  if (!('AspDocumentID' in row && 'AspConnectionID' in row)) return null;

  return typeof row.AspDocumentID === 'number' && typeof row.AspConnectionID === 'number'
    ? {
        AspDocumentID: row.AspDocumentID,
        AspContactID: row.AspContactID ?? null,
        AspConnectionID: row.AspConnectionID,
        AspClientAccountID: row.AspClientAccountID ?? null,
        AspPaymentMethods: row.AspPaymentMethods ?? [],
        AspFailedPayment: row.AspFailedPayment ?? null,
      }
    : null;
}

// PayInvoice writes 'partially-paid'; the synced provider status and the UI use
// 'partiallyPaid'. 'failed' renders as open — the payment row carries the failure.
function normalizeStatus(
  status: AspDocumentStatus | null,
): 'partiallyPaid' | Exclude<AspDocumentStatus, 'partially-paid' | 'failed'> {
  if (!status) return 'open';
  if (status === 'partially-paid') return 'partiallyPaid';
  if (status === 'failed') return 'open';

  return status;
}

// The dialogs and availability filters read banking data off the invoice's
// Customer object (legacy shape). In the asp world payment methods live on the
// contact's client account, so a minimal Customer is synthesized from them —
// enough for the availability flags and masked dropdown labels. Editing
// methods happens against the client account, not through the pay payload.
// Methods are fetched lazily (per action, not per list load), so this is also
// used to enrich a row once its contact's methods arrive.
export function buildCustomerFromPaymentMethods(
  customerName: string | null,
  paymentMethods: PaymentMethod[],
): BillOrInvoiceRailz['Customer'] {
  // Default only, no fallback to the first match: the pay pipeline resolves the
  // contact's method with `IsDefault => 1` (VIORailzIntegrationHelper::
  // createAspInvoicePayment), so a non-default method would offer a rail the
  // backend can't fulfil — it posts the document as paid, then moves no money.
  const byType = (type: PaymentMethod['PaymentMethod']) =>
    paymentMethods.find((method) => method.PaymentMethod === type && method.IsDefault);

  const bank = byType('BankAccount');
  const interacEmail = byType('Email');
  const interacPhone = byType('PhoneNumber');
  const creditCard = byType('CreditCard');
  const debitCard = byType('DebitCard');

  return {
    Name: customerName || undefined,
    Email: interacEmail?.PaymentDetails,
    Phone: interacPhone?.PaymentDetails,
    BankAccount: bank
      ? {
          // PaymentDetails is the only identifier the payment-methods endpoint
          // returns, already masked by the backend — display it as sent rather
          // than padding the institution/transit segments the endpoint omits.
          AccountNumber: bank.PaymentDetails,
          Token: bank.Token,
        }
      : undefined,
    Interac:
      interacEmail || interacPhone
        ? {
            RecipientEmailAddress: interacEmail?.PaymentDetails,
            RecipientPhoneNumber: interacPhone?.PaymentDetails,
          }
        : undefined,
    CreditCard: creditCard
      ? {
          CreditCardToken: creditCard.Token,
          CreditCardNumber: creditCard.PaymentDetails,
          CreditCardBrand: creditCard.ExtraFields.CardBrand,
        }
      : undefined,
    DebitCard: debitCard
      ? {
          PushToCardToken: debitCard.Token,
          CardNumber: debitCard.PaymentDetails,
        }
      : undefined,
  };
}

// The tabs/dialogs still consume the legacy railz row shape; both asp sources
// (invoices and bills) normalize into it here.
export function toAccountingRow(
  invoice: AspInvoice,
  paymentMethods: PaymentMethod[],
  connectionId: number,
  clientAccountId: string | null = null,
): AspAccountingRow {
  const scheduledPayment = invoice.PaymentDetails.find(
    (payment) =>
      payment.Status === 'processing' && !!payment.ScheduledDate && new Date(payment.ScheduledDate) > new Date(),
  );

  const latestPayment = invoice.PaymentDetails.reduce<AspPaymentDetail | null>(
    (latest, payment) => (!latest || payment.PaymentID > latest.PaymentID ? payment : latest),
    null,
  );

  return {
    AspDocumentID: invoice.InvoiceID,
    AspContactID: invoice.ContactID,
    AspConnectionID: connectionId,
    AspClientAccountID: clientAccountId,
    AspPaymentMethods: paymentMethods,
    AspFailedPayment:
      latestPayment?.Status === 'failed'
        ? { TransactionID: latestPayment.TransactionID, TransactionDate: latestPayment.TransactionDate }
        : null,
    id: String(invoice.InvoiceID),
    AccountRef: '',
    invoiceNumber: invoice.InvoiceNumber || '',
    vendorInvoiceNumber: invoice.InvoiceNumber || '',
    postedDate: invoice.PostedDate || '',
    dueDate: invoice.DueDate || '',
    currency: invoice.Currency || 'CAD',
    totalAmount: String(invoice.DueAmount),
    subTotal: String(invoice.DueAmount),
    // Fixed to 2dp: both operands are 2dp decimals, but the binary-float
    // subtraction leaks artifacts (100.1 - 81.54 -> 18.559999999999995) and
    // this string is both displayed and posted as the pay call's PaidAmount.
    amountDue: (invoice.DueAmount - invoice.PaidAmount).toFixed(2),
    status: normalizeStatus(invoice.ASPStatus),
    FailureReason: '',
    ScheduledDate: scheduledPayment?.ScheduledDate || '',
    customerRef: { id: invoice.ContactRefID || '', name: invoice.CustomerName || '' },
    Customer: buildCustomerFromPaymentMethods(invoice.CustomerName, paymentMethods),
    PaymentDetails: invoice.PaymentDetails.map((payment) => ({
      Success: true,
      ErrorMessage: '',
      CreditAmount: String(payment.CreditNoteAmount),
      PaymentMethod: payment.PaymentMethod || undefined,
      PaymentRail: payment.PaymentRail,
      Status: payment.Status,
      TransactionAmount: String(payment.Amount),
      TransactionDate: payment.TransactionDate || '',
      TransactionID: payment.TransactionID === null ? undefined : String(payment.TransactionID),
    })),
  };
}

// Bills share the invoice wire shape modulo field names, so they map through
// toAccountingRow; the payable dialogs read the vendor from vendorRef.
export function toAccountingBillRow(
  bill: AspBill,
  paymentMethods: PaymentMethod[],
  connectionId: number,
  clientAccountId: string | null = null,
): AspAccountingRow {
  const row = toAccountingRow(
    {
      InvoiceID: bill.BillID,
      InvoiceRefID: bill.BillRefID,
      InvoiceNumber: bill.BillNumber,
      ContactID: bill.ContactID,
      ContactRefID: bill.ContactRefID,
      CustomerName: bill.VendorName,
      DueAmount: bill.DueAmount,
      PaidAmount: bill.PaidAmount,
      Currency: bill.Currency,
      CurrencyRate: bill.CurrencyRate,
      PostedDate: bill.PostedDate,
      DueDate: bill.DueDate,
      ASPStatus: bill.ASPStatus,
      LastSyncDate: bill.LastSyncDate,
      DateAdded: bill.DateAdded,
      LastModified: bill.LastModified,
      PaymentDetails: bill.PaymentDetails,
    },
    paymentMethods,
    connectionId,
    clientAccountId,
  );

  return { ...row, vendorRef: { id: bill.ContactRefID || '', name: bill.VendorName || '' } };
}
